import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarList, DistributionBar, InlineTrend, MeterBar, Movement, TrendChart } from '../../../components/design-system/analytics';
import { KpiCard, PillTabs, SearchField, SectionCard, TablePagination, formatNumber } from '../../../components/design-system/admin';
import { MarketData, MarketKeyword } from '../../../common/hooks/use-market-api.hook';
import {
    INTENTS,
    IntentKey,
    KEYWORD_SEGMENTS,
    KeywordSegment,
    hasMovement,
    historyChange,
    historyTrend,
    inSegment,
    intentSplit,
    landingPages,
    segmentCounts,
} from '../../../modules/vendor/market/market-analysis';
import { searchInsights } from '../../../modules/vendor/market/market-insights';
import { monthLabel, seriesOf } from '../../../modules/vendor/market/market-presentation';
import { MovementCard, RankingCard } from './overview-view';
import { UnlockRows } from '../../../components/design-system/plan/UnlockRows';
import { Delta, Head, OutLink, Reading, SortHead, TableFrame, Term, sortRows, usd, useSort } from './market-shared';

type SortKey = 'keyword' | 'position' | 'volume' | 'difficulty' | 'cpc' | 'visits' | 'value';
const PAGE_SIZE = 25;

const isSegment = (v: string | null): v is KeywordSegment => !!v && (KEYWORD_SEGMENTS as string[]).includes(v);

/**
 * The searches the vendor is found for.
 *
 * The reading first (what moved, what is nearly there), then where the
 * rankings sit and which pages and intentions bring the visits, then every
 * keyword — filterable by the segments the reading talks about, so each
 * sentence above has its rows one click away.
 */
export const SearchView: React.FC<{ data: MarketData }> = ({ data }) => {
    const { t, i18n } = useTranslation();
    const [params, setParams] = useSearchParams();
    const o = data.overview;
    const keywords = useMemo(() => data.keywords?.keywords ?? [], [data.keywords]);

    const [segment, setSegment] = useState<KeywordSegment>(isSegment(params.get('segment')) ? (params.get('segment') as KeywordSegment) : 'all');
    const [intent, setIntent] = useState<IntentKey | 'all'>('all');
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(0);
    const { sort, toggle } = useSort<SortKey>('visits');

    const tableRef = useRef<HTMLDivElement>(null);
    // Set when the change came from the pills, which are already on screen.
    const fromPills = useRef(false);
    // A link to a segment (from a reading here or on another page) lands on
    // its rows: the segment is chosen and the table brought into view.
    useEffect(() => {
        const s = params.get('segment');
        if (isSegment(s)) {
            setSegment(s);
            if (!fromPills.current) tableRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
        }
        fromPills.current = false;
    }, [params]);
    useEffect(() => setPage(0), [segment, intent, query, sort]);

    const choose = (s: KeywordSegment) => {
        fromPills.current = true;
        setSegment(s);
        const next = new URLSearchParams(params);
        if (s === 'all') next.delete('segment');
        else next.set('segment', s);
        setParams(next, { replace: true });
    };

    const specs = useMemo(() => searchInsights(data), [data]);
    const counts = useMemo(() => segmentCounts(keywords), [keywords]);
    const moving = hasMovement(keywords);
    const pages = useMemo(() => landingPages(keywords, 6), [keywords]);
    const intents = useMemo(() => intentSplit(keywords, 'visits'), [keywords]);
    const knownIntent = intents.some((i) => i.key !== 'unknown' && i.value > 0);

    const history = data.history?.overview;
    const since = (key: string) => {
        const ch = historyChange(history, key);
        return ch ? { delta: { current: ch.current, previous: ch.previous }, since: t('BUSINESS.COMMON.SINCE', { month: monthLabel(ch.since, i18n.language) }) } : null;
    };
    const hint = (parts: (string | null | undefined)[]) => parts.filter(Boolean).join(' · ');
    const kw = since('organicKeywords');
    const p1 = since('top10');
    const visits = since('estimatedMonthlyVisits');
    const value = since('trafficValue');
    const visitSeries = seriesOf(history, 'estimatedMonthlyVisits').map((p) => ({ label: monthLabel(p.month, i18n.language), value: p.value }));

    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = keywords.filter(
            (k) =>
                inSegment(k, segment) &&
                (intent === 'all' || (intent === 'unknown' ? !k.intent : k.intent === intent)) &&
                (!q || (k.keyword ?? '').toLowerCase().includes(q) || (k.url ?? '').toLowerCase().includes(q)),
        );
        const get: Record<SortKey, (k: MarketKeyword) => number | string | null | undefined> = {
            keyword: (k) => k.keyword,
            position: (k) => k.position,
            volume: (k) => k.searchVolume,
            difficulty: (k) => k.difficulty,
            cpc: (k) => k.cpc,
            visits: (k) => k.estimatedVisits,
            value: (k) => k.trafficValue,
        };
        return sortRows(filtered, get[sort.key], sort.dir);
    }, [keywords, segment, intent, query, sort]);

    const segments = KEYWORD_SEGMENTS.filter((s) => moving || !['up', 'down', 'new'].includes(s)).map((s) => ({
        value: s,
        label: t(`BUSINESS.SEARCH.SEGMENT.${s.toUpperCase()}`),
        count: counts[s],
    }));

    return (
        <div className="flex flex-col gap-6">
            <Reading specs={specs} />

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard
                    label={t('BUSINESS.SEARCH.KPI.KEYWORDS')}
                    value={formatNumber(o?.organicKeywords)}
                    delta={kw?.delta}
                    hint={hint([kw?.since, t('BUSINESS.SEARCH.KPI.KEYWORDS_HINT')])}
                    trend={historyTrend(history, 'organicKeywords')}
                />
                <KpiCard
                    label={t('BUSINESS.SEARCH.KPI.PAGE_ONE')}
                    value={formatNumber(o?.top10)}
                    delta={p1?.delta}
                    hint={hint([p1?.since, t('BUSINESS.SEARCH.KPI.TOP3', { n: formatNumber(o?.top3) })])}
                    trend={historyTrend(history, 'top10')}
                />
                <KpiCard
                    label={t('BUSINESS.SEARCH.KPI.VISITS')}
                    value={formatNumber(o?.estimatedMonthlyVisits)}
                    delta={visits?.delta}
                    hint={hint([visits?.since, t('BUSINESS.OVERVIEW.KPI.VISITS_HINT')])}
                    trend={historyTrend(history, 'estimatedMonthlyVisits')}
                />
                <KpiCard
                    label={t('BUSINESS.OVERVIEW.KPI.VALUE')}
                    value={o?.trafficValue != null ? usd(o.trafficValue) : '—'}
                    delta={value?.delta}
                    hint={hint([value?.since, t('BUSINESS.OVERVIEW.KPI.VALUE_HINT')])}
                    trend={historyTrend(history, 'trafficValue')}
                />
            </div>

            <div className="grid gap-4 lg:grid-cols-5">
                <RankingCard data={data} className="lg:col-span-3" showLink={false} />
                {visitSeries.length > 1 ? (
                    <SectionCard className="lg:col-span-2" title={t('BUSINESS.SEARCH.TREND')} description={t('BUSINESS.SEARCH.TREND_HINT')}>
                        <TrendChart data={visitSeries} valueLabel={t('BUSINESS.SEARCH.KPI.VISITS')} height={200} />
                    </SectionCard>
                ) : (
                    <MovementCard data={data} className="lg:col-span-2" />
                )}
            </div>

            {(pages.length > 0 || knownIntent) && (
                <div className="grid gap-4 lg:grid-cols-2">
                    {pages.length > 0 && (
                        <SectionCard title={t('BUSINESS.SEARCH.PAGES')} description={t('BUSINESS.SEARCH.PAGES_HINT')}>
                            <BarList
                                items={pages.map((p) => ({
                                    key: p.url,
                                    label: (
                                        <span className="block min-w-0">
                                            <span className="block truncate" title={p.fullUrl ?? p.url}>
                                                {p.url}
                                            </span>
                                        </span>
                                    ),
                                    value: p.visits,
                                    secondary: t('BUSINESS.SEARCH.PAGE_KEYWORDS', { count: p.keywords, n: formatNumber(p.keywords) }),
                                }))}
                            />
                        </SectionCard>
                    )}
                    {knownIntent && (
                        <SectionCard title={<Term k="INTENT">{t('BUSINESS.SEARCH.INTENT')}</Term>} description={t('BUSINESS.SEARCH.INTENT_HINT')}>
                            <DistributionBar
                                columns={2}
                                segments={intents.map((s) => ({ key: s.key, label: t(`BUSINESS.INTENT.${s.key.toUpperCase()}`), value: s.value }))}
                            />
                            <ul className="mt-4 flex flex-col gap-1.5 border-t pt-3">
                                {intents
                                    .filter((s) => s.key !== 'unknown')
                                    .map((s) => (
                                        <li key={s.key} className="text-xs leading-relaxed text-muted-foreground">
                                            <span className="font-medium text-foreground">{t(`BUSINESS.INTENT.${s.key.toUpperCase()}`)}</span> —{' '}
                                            {t(`BUSINESS.INTENT.${s.key.toUpperCase()}_HINT`)}
                                        </li>
                                    ))}
                            </ul>
                        </SectionCard>
                    )}
                </div>
            )}

            <div ref={tableRef} className="scroll-mt-6">
                <SectionCard
                    flush
                    title={t('BUSINESS.SEARCH.TABLE')}
                    description={t('BUSINESS.SEARCH.TABLE_HINT', {
                        shown: formatNumber(keywords.length),
                        total: formatNumber(data.keywords?.total ?? o?.organicKeywords ?? keywords.length),
                    })}
                    testId="business-keywords"
                >
                    <div className="flex flex-col gap-3 px-4 pb-3">
                        <PillTabs value={segment} options={segments} onChange={choose} ariaLabel={t('BUSINESS.SEARCH.TABLE')} />
                        <div className="flex flex-wrap items-center gap-2">
                            <SearchField value={query} onChange={setQuery} placeholder={t('BUSINESS.SEARCH.FILTER')} />
                            {knownIntent && (
                                <Select value={intent} onValueChange={(v) => setIntent(v as IntentKey | 'all')}>
                                    <SelectTrigger size="sm" className="h-9 w-auto min-w-40" aria-label={t('BUSINESS.TERMS.INTENT.LABEL')}>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('BUSINESS.INTENT.ALL')}</SelectItem>
                                        {INTENTS.map((i) => (
                                            <SelectItem key={i} value={i}>
                                                {t(`BUSINESS.INTENT.${i.toUpperCase()}`)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                        </div>
                    </div>

                    {keywords.length === 0 ? (
                        <p className="border-t px-4 py-10 text-center text-sm text-muted-foreground">{t('BUSINESS.OVERVIEW.NO_KEYWORDS')}</p>
                    ) : (
                        <>
                            <TableFrame className="border-t">
                                <thead className="border-b text-xs text-muted-foreground">
                                    <tr>
                                        <SortHead align="start" label={t('BUSINESS.COLUMN.KEYWORD')} active={sort.key === 'keyword'} dir={sort.dir} onClick={() => toggle('keyword', 'asc')} />
                                        <SortHead label={t('BUSINESS.COLUMN.POSITION')} term="POSITION" active={sort.key === 'position'} dir={sort.dir} onClick={() => toggle('position', 'asc')} />
                                        <SortHead label={t('BUSINESS.COLUMN.VOLUME')} active={sort.key === 'volume'} dir={sort.dir} onClick={() => toggle('volume')} />
                                        <SortHead label={t('BUSINESS.COLUMN.DIFFICULTY')} term="DIFFICULTY" active={sort.key === 'difficulty'} dir={sort.dir} onClick={() => toggle('difficulty', 'asc')} />
                                        <Head term="INTENT">{t('BUSINESS.COLUMN.INTENT')}</Head>
                                        <SortHead label={t('BUSINESS.COLUMN.CPC')} term="CPC" active={sort.key === 'cpc'} dir={sort.dir} onClick={() => toggle('cpc')} />
                                        <SortHead label={t('BUSINESS.COLUMN.VISITS')} term="VISITS" active={sort.key === 'visits'} dir={sort.dir} onClick={() => toggle('visits')} />
                                        <SortHead label={t('BUSINESS.COLUMN.VALUE')} term="TRAFFIC_VALUE" active={sort.key === 'value'} dir={sort.dir} onClick={() => toggle('value')} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((k, i) => (
                                        <KeywordRow key={`${k.keyword}-${i}`} k={k} />
                                    ))}
                                    {!rows.length && (
                                        <tr>
                                            <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                                                {t('BUSINESS.SEARCH.NO_MATCH')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </TableFrame>
                            {rows.length > PAGE_SIZE && <TablePagination page={page} pageSize={PAGE_SIZE} total={rows.length} onPageChange={setPage} className="border-t" />}
                        </>
                    )}
                    {/* The keywords the plan cut, where they would have been. */}
                    <UnlockRows count={data.locked?.keywords} noun="keywords" reason="maxKeywordRows" testId="unlock-keywords" />
                </SectionCard>
            </div>
        </div>
    );
};

const KeywordRow: React.FC<{ k: MarketKeyword }> = ({ k }) => {
    const { t } = useTranslation();
    return (
        <tr className="border-b last:border-b-0 hover:bg-muted/30">
            <td className="max-w-[280px] p-3 align-top">
                <span className="flex items-center gap-1.5">
                    <span className="truncate font-medium">{k.keyword}</span>
                    {k.featuredSnippet && (
                        <span className="shrink-0 rounded-full border px-1.5 text-[10px] leading-4 text-muted-foreground">{t('BUSINESS.SEARCH.SNIPPET')}</span>
                    )}
                </span>
                {(k.fullUrl || k.url) && (
                    <OutLink href={k.fullUrl || `${k.url}`} className="mt-0.5 text-xs text-muted-foreground" title={k.title ?? undefined}>
                        {k.url || k.fullUrl}
                    </OutLink>
                )}
            </td>
            <td className="p-3 text-end align-top whitespace-nowrap">
                <span className="inline-flex items-center justify-end gap-1.5">
                    <span className="tabular-nums">{k.position ?? '—'}</span>
                    {k.movement && <Movement kind={k.movement === 'new' ? 'new' : undefined} from={k.previousPosition} to={k.position} />}
                </span>
            </td>
            <td className="p-3 text-end align-top whitespace-nowrap">
                <span className="inline-flex items-center justify-end gap-2">
                    <InlineTrend data={k.trend} className="hidden sm:block" />
                    <span className="tabular-nums">{formatNumber(k.searchVolume)}</span>
                </span>
                {k.yearlyChange != null && (
                    <span className="mt-0.5 flex justify-end">
                        <Delta value={k.yearlyChange} />
                    </span>
                )}
            </td>
            <td className="p-3 text-end align-top">
                <MeterBar value={k.difficulty} invert width="w-10" />
            </td>
            <td className={cn('p-3 text-end align-top text-xs whitespace-nowrap', k.intent ? 'text-foreground' : 'text-muted-foreground')}>
                {k.intent ? t(`BUSINESS.INTENT.${k.intent.toUpperCase()}`) : '—'}
            </td>
            <td className="p-3 text-end align-top tabular-nums whitespace-nowrap">{k.cpc != null ? usd(k.cpc, 2) : '—'}</td>
            <td className="p-3 text-end align-top tabular-nums">{formatNumber(k.estimatedVisits)}</td>
            <td className="p-3 text-end align-top tabular-nums text-muted-foreground">{k.trafficValue != null ? usd(k.trafficValue) : '—'}</td>
        </tr>
    );
};
